import React, { FC, ReactElement, SyntheticEvent, useEffect, useState, useMemo } from 'react';
import { IconButton, ListItem, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import i18next from 'i18next';
import {
  Cancel, CheckCircle, DriveEta, Error, InfoOutlined, Person, PersonOutlined,
  RadioButtonChecked, DeleteOutline, LockOpen, Lock, RoomOutlined, LocalShippingOutlined, KeyboardArrowDownOutlined, MapOutlined,
  BarChartOutlined, ReceiptLongOutlined, ViewInAr
} from '@mui/icons-material';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, styled } from '@mui/styles';
import { isEqual } from 'underscore';
import { ReactCalendarGroupRendererProps } from 'react-calendar-timeline';
import { push } from 'redux-first-history';
import { useDrop } from 'react-dnd';
import { toastr } from 'react-redux-toastr';

import { dispatch_theme, background, zufrueh } from '@/config/colors';
import { setAggregatedTourView, setReassignedShipments } from '@/actions/routes/routesPure';
import { CNAME } from '@/config/paths';
import Car from '../../Common/Icons/svg/car.svg';
import Van from '../../Common/Icons/svg/van.svg';
import Truck from '../../Common/Icons/svg/truck01.svg';
import Walk from '../../Common/Icons/svg/walk.svg';
import Bike from '../../Common/Icons/svg/bike.svg';
import Nope from '../../Common/Icons/svg/nope01.svg';
import { TimelineGroupCustom } from '@/config/types/Routes';
import { RootState } from '@/index';
import vehicleApi from '@/apis/vehicleApi';
import { ItemTypes } from '../CustomTimeline/types';
import { getLoadClassData } from '@/common/common';

const vehicles: any = {
  car: Car,
  van: Van,
  truck: Truck,
  walk: Walk,
  bike: Bike,
  none: Nope,
};

const groupIcon = (status: number, color: string) => {
  return {
    0: (
      <RadioButtonChecked
        style={{ borderRadius: '50%', width: 13, fill: color, height: 13 }}
      />
    ),
    1: <Person style={{ fontSize: 13, color }} fontSize={'small'} />,
    2: <PersonOutlined style={{ fontSize: 13, color }} fontSize={'small'} />,
    3: <DriveEta style={{ fontSize: 13, color }} fontSize={'small'} />,
    4: <Cancel style={{ fontSize: 13, color }} fontSize={'small'} />,
    5: <Error style={{ fontSize: 13, color }} fontSize={'small'} />,
    6: <CheckCircle style={{ fontSize: 13, color }} fontSize={'small'} />,
  }[status];
};

const useStyles = makeStyles((theme: typeof dispatch_theme) => ({
  overloadedVehicle: {
    color: theme.palette.error.main,
  },
  pointer: {
    cursor: 'pointer',
    paddingLeft: '10px'
  },
  disabledCursor: {
    cursor: 'not-allowed'
  },
  activeTarget: {
    backgroundColor: zufrueh
  },
  listItemTextRoot: {
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
    padding: '0px'
  },
  vehicleName: {
    color: '#000000',
    width: '60%',
    fontSize: '13px',
  },
  rightInfoWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    width: '40%'
  },
  countWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  shipmentIcon: {
    color: '#999',
    fontSize: '20px',
  },
  stopIcon: {
    color: 'grey',
    fontSize: '18px',
  },
  shipmentText: {
    fontWeight: 600,
    color: '#333',
    display: 'inline-block',
    minWidth: '13px',
    textAlign: 'right'
  },
  stopText: {
    fontWeight: 600,
    color: '#999',
    display: 'inline-block',
    minWidth: '13px',
    textAlign: 'right'
  },
  menuItem: {
    padding: '8px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
  listItemIcon: {
    fontSize: '20px',
  },
  weightWatcherIcon: {
    color: '#ef5927',
  },
  vehicleDetailIcon: {
    color: '#3f51b5',
  },
  tourExportIcon: {
    color: '#4caf50',
  },
  MapIcon: {
    color: '#1976d2',
  },
  menuText: {
    fontSize: '14px',
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    height: '23px',
  },
  loadClass: {
    fontSize: '12px',
    marginTop: '3px',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px'
  },
  loadClassIcon: {
    fontSize: '16px',
    marginRight: '5px',
    color: 'grey',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '6px',
    marginBottom: '7px'
  },
  iconButton: {
    padding: 4,
    height: 21,
    width: 21,
  },
  deleteIconWrapper: {
    cursor: 'not-allowed',
  },
  deleteIcon: {
    fontSize: 18,
  },
  icon: {
    fontSize: 24,
    color: 'grey',
  },
  flexContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '10px',
  },
  countTextWrapper: {
    display: 'flex',
    gap: 7,
    marginRight: '10px',
  },
}));

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: background.tertiary['200'],
  },
}));

export const Group: FC<ReactCalendarGroupRendererProps<TimelineGroupCustom>> =
  React.memo(
    (
      props: ReactCalendarGroupRendererProps<TimelineGroupCustom>
    ): ReactElement => {
      const [anchorEl, setMenuAnchor] = useState(undefined);
      const [showVehicleOptions, toggleVehicleOptions] = useState(false);
      const [remainingCapacity, setRemainingCapacity] = useState<number>(0);
      const [isDeleteVehicleDialogOpen, setIsDeleteVehicleDialogOpen] = useState<boolean>(false);
      const { status, vehicle, vehicle_id, id, custom_id, shipments, stops, vehicle_name } =
        props.group.route;
      const classes = useStyles();
      const [routeId, setId] = useState(id);
      // eslint-disable-next-line no-unused-vars
      const [highlight, setHighlight] = useState(false);
      const {
        color = '#e5e5e5',
        toggleWeightWatcher,
        showVehicle,
        showTourExport,
        update,
        //onVehicleLock,
        isLockedVehicle,
        id: groupId,
        display
      } = props.group;
      const dispatch = useDispatch();
      const totalTourLoad = 1000;
      const { selected_process, ui } = useSelector((state: RootState) => state.core, isEqual);
      const { deletedRoutes } = useSelector((state: RootState) => state.routes, isEqual);
      const vehiclesData = useSelector((state: RootState) => state.vehicles.vehiclesData, isEqual);
      //@ts-ignore
      const filteredVehicle = vehiclesData.find((filteredVehicle) => filteredVehicle.name === vehicle_name);
      let loadClassData = getLoadClassData(filteredVehicle);
      const onMultiRouteFocus = (ids: number[]) =>
        dispatch(setAggregatedTourView(ids));

      const handleDrop = (_item: any): void => {
        if (isLockedVehicle) {
          toastr.warning('', `${i18next.t('common:planning.vehicleShort')} ${groupId} ${i18next.t('common:isLocked')}`);
          return;
        }
        dispatch(setReassignedShipments(Number(id), _item));
        toastr.success('', `${i18next.t('common:shipment')} ${_item.id} ${i18next.t('common:wasReassigned')}`);
      };
      const [{ isOver, canDrop }, drop] = useDrop(
        () => ({
          accept: ItemTypes.SHIPMENT,
          drop: handleDrop,
          collect: (monitor) => ({
            isOver: !!monitor.isOver(),
            canDrop: !!monitor.canDrop(),
          })
        }), [handleDrop]
      );
      useEffect(() => {
        if (update === routeId) {
          setHighlight(true);
          setId(id);
          setTimeout(() => {
            setHighlight(false);
          }, 3000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [update]);

      function handleClick(event: SyntheticEvent<any>) {
        if (!event) {
          toggleVehicleOptions(false);
          return setMenuAnchor(null);
        }
        event.preventDefault();
        event.stopPropagation();
        toggleVehicleOptions(true);
        setMenuAnchor(event.target);
      }

      function goToTour() {
        setMenuAnchor(null);
        const encodedVehicleName = encodeURIComponent(vehicle_name);
        dispatch(push(`${CNAME}routes/${encodedVehicleName}`));
      }

      function selectVehicleOption(value: string) {
        if (value === 'weightWatcher') {
          toggleWeightWatcher(props.group);
        } else if (value === 'tourExport') {
          showTourExport();
        } else {
          showVehicle(value);
        }
        toggleVehicleOptions(false);
        setMenuAnchor(null);
      }

      const toggleVehicleDialog = () => {
        setIsDeleteVehicleDialogOpen(!isDeleteVehicleDialogOpen);
      };

      const icon = groupIcon(status, color);
      const TooltipElement = remainingCapacity < 0 ? CustomTooltip : Tooltip;
      const LockIcon = useMemo(() => isLockedVehicle ? Lock : LockOpen, [isLockedVehicle]);

      const isActiveDropTarget = !isOver && canDrop;
      const isUpdatePlanningFlag = ui.includes('update_planning');

      const renderGroupInfo = () => (
        <ListItemText
          classes={{ root: classes.pointer }}
          primaryTypographyProps={{ className: classes.listItemTextRoot }}
          primary={
            <>
              <div className={classes.flexContainer}>
                <span
                  className={`${classes.vehicleName} ${isUpdatePlanningFlag && remainingCapacity < 0
                    ? classes.overloadedVehicle
                    : ''
                    }`}
                >
                  {vehicle_name}
                </span>
                <span className={classes.countTextWrapper}>
                  <span className={classes.countWrapper}>
                    <ViewInAr className={classes.shipmentIcon} />
                    <span className={classes.shipmentText}>{display.totalShipments}</span>
                  </span>
                  <span className={classes.countWrapper}>
                    <RoomOutlined className={classes.stopIcon} />
                    <span className={classes.stopText}>{display.totalStops}</span>
                  </span>
                </span>
              </div>
              <div className={classes.container}>
                <Typography variant="body2" className={classes.loadClass}>
                  {loadClassData && loadClassData !== "DEFAULT" && (
                    <LocalShippingOutlined className={classes.loadClassIcon} />
                  )}
                  {loadClassData}
                </Typography>
                <span className={classes.iconContainer}>
                  <Tooltip
                    title={
                      <Typography style={{ fontSize: 12 }} color="white">
                        {i18next.t('common:routes.showVehicleInfo')}
                      </Typography>
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={handleClick}
                      data-cy="vehicle-info-button"
                      className={classes.iconButton}
                    >
                      <KeyboardArrowDownOutlined className={classes.icon} />
                    </IconButton>
                  </Tooltip>
                  {isUpdatePlanningFlag && (
                    <Tooltip
                      title={
                        <Typography style={{ fontSize: 12 }} color="white">
                          {i18next.t('common:routes.deleteVehicle')}
                        </Typography>
                      }
                    >
                      <span className={isLockedVehicle ? classes.deleteIconWrapper : ''}>
                        <IconButton
                          size="small"
                          onClick={toggleVehicleDialog}
                          id={`delete-vehicle-${id}`}
                          disabled={isLockedVehicle}
                          className={classes.iconButton}
                        >
                          <DeleteOutline color="inherit" className={classes.deleteIcon} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </span>
              </div>
            </>
          }
        />
      );

      return (
        <React.Fragment>
          <ListItem
            ref={drop}
            style={{ height: '100%', flexDirection: 'column', alignItems: 'flex-start', padding: '0px' }}
            classes={{ root: isActiveDropTarget ? classes.activeTarget : '' }}
            dense={true}
            disableGutters={true}
          >
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', height: '43px' }}>
              {isUpdatePlanningFlag && (
                <Tooltip
                  title={
                    <Typography style={{ fontSize: 12 }} color={'white'}>
                      {isLockedVehicle
                        ? i18next.t('common:routes.unlockVehicle')
                        : i18next.t('common:routes.lockVehicle')}
                    </Typography>
                  }
                >
                  <IconButton
                    size="small"
                    //onClick={onVehicleLock}
                    data-vehicleid={vehicle_id}
                    data-vehiclename={custom_id}
                    data-cy="vehicle-lock-button"
                  >
                    <LockIcon
                      color={isLockedVehicle ? 'primary' : 'inherit'}
                      fontSize="small"
                      style={{ fontSize: 13 }}
                    />
                  </IconButton>
                </Tooltip>
              )}
              {isUpdatePlanningFlag ? (
                <TooltipElement
                  title={
                    <Typography style={{ fontSize: 12 }} color="white">
                      <span
                        className={
                          remainingCapacity < 0 ? classes.overloadedVehicle : ''
                        }
                      >
                        {`${i18next.t('common:remainingCapacity')}: ${remainingCapacity} kg`}
                      </span>
                    </Typography>
                  }
                  classes={{
                    tooltip:
                      remainingCapacity < 0 ? classes.overloadedVehicle : undefined,
                  }}
                  placement="top"
                >
                  {renderGroupInfo()}
                </TooltipElement>
              ) : (
                renderGroupInfo()
              )}
            </div>
          </ListItem>
          <Menu
            anchorEl={anchorEl}
            MenuListProps={{
              style: {
                display: 'flex',
                flexDirection: 'column',
                padding: '8px 0',
              },
            }}
            open={Boolean(showVehicleOptions && anchorEl)}
            onClose={() => handleClick(null)}
          >
            <MenuItem
              value={'weightWatcher'}
              data-cy={'weightWatcherOption'}
              onClick={() => selectVehicleOption('weightWatcher')}
              className={classes.menuItem}
            >
              <ListItemIcon>
                <BarChartOutlined className={`${classes.listItemIcon} ${classes.weightWatcherIcon}`} />
              </ListItemIcon>
              <ListItemText disableTypography>
                <Typography variant="body2" className={classes.menuText}>
                  {i18next.t('common:routes.showWeightWatcher')}
                </Typography>
              </ListItemText>
            </MenuItem>
            <MenuItem
              data-cy={'vehicleDetailsOption'}
              value={vehicle_name}
              onClick={() => selectVehicleOption(vehicle_name)}
              className={classes.menuItem}
            >
              <ListItemIcon>
                <InfoOutlined className={`${classes.listItemIcon} ${classes.vehicleDetailIcon}`} />
              </ListItemIcon>
              <ListItemText disableTypography>
                <Typography variant="body2" className={classes.menuText}>
                  {i18next.t('common:routes.showVehicleDetail')}
                </Typography>
              </ListItemText>
            </MenuItem>
            <MenuItem
              data-cy={'tourExportOption'}
              value={vehicle_id}
              onClick={() => selectVehicleOption('tourExport')}
              className={classes.menuItem}
            >
              <ListItemIcon>
                <ReceiptLongOutlined className={`${classes.listItemIcon} ${classes.tourExportIcon}`} />
              </ListItemIcon>
              <ListItemText disableTypography>
                <Typography variant="body2" className={classes.menuText}>
                  {i18next.t('common:buttons.tourExport')}
                </Typography>
              </ListItemText>
            </MenuItem>
            <MenuItem
              data-cy={`showTourOption-${id}`}
              onClick={goToTour}
              className={classes.menuItem}
            >
              <Tooltip
                title={
                  <Typography style={{ fontSize: 12 }} color="white">
                    {i18next.t('common:routes.showTourOnMap')}
                  </Typography>
                }
              >
                <ListItemIcon>
                  <MapOutlined className={`${classes.listItemIcon} ${classes.MapIcon}`} />
                </ListItemIcon>
              </Tooltip>
              <ListItemText disableTypography>
                <Typography variant="body2" className={classes.menuText}>
                  {i18next.t('common:routes.showTourOnMap')}
                </Typography>
              </ListItemText>
            </MenuItem>
          </Menu>
        </React.Fragment>
      );
    },
    isEqual
  );
